/**
 * PWAInstallPrompt — bottom-sheet install prompt.
 *
 * Design rules:
 *   - uncodixify-ui.md: no hover transforms, no rounded-full on container,
 *     no animate-bounce; transition-colors only.
 *   - rtl-support-arabic.md: logical CSS (ps-*, pe-*, ms-*, me-*).
 *   - accessibility: focus-trapped via vaul Drawer, aria-labelledby, touch
 *     targets ≥ 44×44 px achieved by button padding.
 *   - pwa-patterns.md: never show before valueMomentReached; iOS guide variant.
 *
 * Callers must pass i18n strings; this component is locale-agnostic.
 */

import { useEffect } from "react";
import { Drawer } from "vaul";
import { usePWAInstall } from "#/hooks/usePWAInstall.js";
import { usePWAValueMoment } from "#/hooks/usePWAValueMoment.js";

export interface PWAInstallPromptStrings {
  title: string;
  subtitle: string;
  cta: string;
  dismiss: string;
  iosStep1: string;
  iosStep2: string;
  iosStep3: string;
}

interface PWAInstallPromptProps {
  strings: PWAInstallPromptStrings;
  appName: string;
}

function IOSGuide({
  strings,
  onDismiss,
}: {
  strings: PWAInstallPromptStrings;
  onDismiss: () => void;
}) {
  return (
    <div className="space-y-4">
      <ol className="space-y-3 text-[var(--color-fg-default)]">
        {[strings.iosStep1, strings.iosStep2, strings.iosStep3].map(
          (step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                aria-hidden
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-info)] text-xs font-bold text-white"
              >
                {i + 1}
              </span>
              <span className="text-sm leading-relaxed">{step}</span>
            </li>
          ),
        )}
      </ol>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-2 w-full rounded-md border border-[var(--color-border-default)] px-4 py-3 text-sm font-medium text-[var(--color-fg-muted)] transition-colors duration-150 hover:bg-[var(--color-bg-surface-elevated)] min-h-[44px]"
      >
        {strings.dismiss}
      </button>
    </div>
  );
}

export function PWAInstallPrompt({ strings, appName: _ }: PWAInstallPromptProps) {
  const { valueMomentReached } = usePWAValueMoment();
  const { canInstall, showIOSGuide, prompt, dismiss, isInstalled } =
    usePWAInstall();

  const shouldShow =
    !isInstalled && valueMomentReached && (canInstall || showIOSGuide);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Expose dismiss/prompt helpers for e2e tests.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__pwaInstall = { prompt, dismiss };
  }, [prompt, dismiss]);

  return (
    <Drawer.Root
      open={shouldShow}
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
      modal
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Drawer.Content
          data-testid="pwa-install-prompt"
          aria-labelledby="pwa-install-title"
          className={[
            "fixed inset-x-0 bottom-0 z-50 flex flex-col",
            "rounded-t-xl bg-[var(--color-bg-surface)]",
            "border-t border-[var(--color-border-default)]",
            "focus:outline-none",
            "max-h-[80dvh]",
          ].join(" ")}
        >
          {/* Drag handle */}
          <div
            aria-hidden
            className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-[var(--color-border-default)]"
          />

          {/* Header */}
          <div className="flex items-start justify-between px-5 pb-2 pt-4">
            <Drawer.Title
              id="pwa-install-title"
              className="text-base font-semibold text-[var(--color-fg-default)]"
            >
              {strings.title}
            </Drawer.Title>
            <button
              type="button"
              onClick={dismiss}
              aria-label={strings.dismiss}
              className="flex items-center justify-center rounded-md p-2 min-h-[44px] min-w-[44px] text-[var(--color-fg-muted)] transition-colors duration-150 hover:bg-[var(--color-bg-surface-elevated)]"
            >
              <svg
                aria-hidden
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="h-4 w-4"
              >
                <path d="M2 2l12 12M14 2L2 14" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] pt-1">
            {showIOSGuide ? (
              <IOSGuide strings={strings} onDismiss={dismiss} />
            ) : (
              <div className="space-y-4">
                <p className="text-sm leading-relaxed text-[var(--color-fg-muted)]">
                  {strings.subtitle}
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={prompt}
                    className="flex-1 rounded-md bg-[var(--color-accent-emergency)] px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:opacity-90 min-h-[44px]"
                  >
                    {strings.cta}
                  </button>
                  <button
                    type="button"
                    onClick={dismiss}
                    className="flex-1 rounded-md border border-[var(--color-border-default)] px-4 py-3 text-sm font-medium text-[var(--color-fg-muted)] transition-colors duration-150 hover:bg-[var(--color-bg-surface-elevated)] min-h-[44px]"
                  >
                    {strings.dismiss}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
